import { MARKET_ANALYSIS_SECTIONS } from './constants';
import { getIconForSection } from './reportUtils';

// MARKET_ANALYSIS_SECTIONS is now exported from constants.js for better organization
// Re-export it here for backwards compatibility
export { MARKET_ANALYSIS_SECTIONS };

/**
 * Safely format a numeric value, handling null/undefined
 * @param {number|null} value - Value to format
 * @param {Function} formatter - Formatting function
 * @param {string} fallback - Fallback string for null/undefined
 * @returns {string} Formatted value or fallback
 */
function safeFormat(value, formatter, fallback = 'N/A') {
  if (value === null || value === undefined) {
    return fallback;
  }
  try {
    return formatter(value);
  } catch {
    return fallback;
  }
}

/**
 * Format currency in billions
 * @param {number} value - Value in dollars
 * @returns {string} Formatted string
 */
function formatBillions(value) {
  return `$${(value / 1000000000).toFixed(1)}B`;
}

/**
 * Format percentage from decimal
 * @param {number} value - Decimal value (e.g., 0.06 for 6%)
 * @returns {string} Formatted percentage string
 */
function formatPercent(value) {
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Format currency in millions
 * @param {number} value - Value in dollars
 * @returns {string} Formatted string
 */
function formatMillions(value) {
  return `$${(value / 1000000).toFixed(1)}M`;
}

/**
 * Transforms market analysis report data into sections array format for ReportPanel
 * Uses markdown for most content, tables for tabular data
 *
 * @param {object} data - Market analysis report data
 * @returns {Array} Array of section objects compatible with ReportPanel
 */
export function transformMarketAnalysisToSections(data) {
  if (!data) return [];

  const sections = [];

  // Executive Summary - markdown with subsections
  if (data.executiveSummary?.overview) {
    const subsections = [];

    // Add confidence level if available
    if (data.executiveSummary.confidenceLevel) {
      const confidenceColors = {
        high: '🟢',
        medium: '🟡',
        low: '🔴'
      };
      const confidenceIcon =
        confidenceColors[data.executiveSummary.confidenceLevel.toLowerCase()] || '';

      let confidenceContent = `${confidenceIcon} **Data Confidence:** ${data.executiveSummary.confidenceLevel}`;
      if (data.executiveSummary.confidenceExplanation) {
        confidenceContent += `\n\n${data.executiveSummary.confidenceExplanation}`;
      }

      subsections.push({
        component: 'markdown',
        content: confidenceContent
      });
    }

    // Add Key Findings subsection if available
    if (data.executiveSummary.keyFindings?.length > 0) {
      subsections.push({
        title: 'Key Findings',
        component: 'bullet-list',
        content: data.executiveSummary.keyFindings
      });
    }

    // Add Key Highlights subsection if available
    if (data.executiveSummary.keyHighlights) {
      const highlights = [];

      // Top Opportunity first
      if (data.executiveSummary.keyHighlights.topOpportunity) {
        const opp = data.executiveSummary.keyHighlights.topOpportunity;
        highlights.push({
          primary: `Top Opportunity: ${opp.title}`,
          secondary: opp.description
        });
      }

      // Key Challenge second
      if (data.executiveSummary.keyHighlights.keyChallenge) {
        const challenge = data.executiveSummary.keyHighlights.keyChallenge;
        highlights.push({
          primary: `Key Challenge: ${challenge.title}`,
          secondary: challenge.description
        });
      }

      if (highlights.length > 0) {
        subsections.push({
          title: 'Key Highlights',
          component: 'bullet-list',
          content: highlights
        });
      }
    }

    sections.push({
      id: 'executiveSummary',
      title: 'Executive Summary',
      component: 'markdown',
      content: data.executiveSummary.overview,
      subsections,
      icon: getIconForSection('executiveSummary')
    });
  }

  // Market Overview - markdown
  if (data.marketOverview?.overview) {
    sections.push({
      id: 'marketOverview',
      title: 'Market Overview',
      component: 'markdown',
      content: data.marketOverview.overview,
      icon: getIconForSection('marketOverview')
    });
  }

  // Market Growth Trend - with stats and chart
  if (data.marketGrowthTrend) {
    const subsections = [];

    // Add stat cards if metrics available
    if (data.marketGrowthTrend.metrics) {
      const {
        cagr,
        latestRevenue,
        annualGrowthRate,
        cagrDisplay,
        latestRevenueDisplay,
        annualGrowthRateDisplay
      } = data.marketGrowthTrend.metrics;

      // Build stats array, filtering out unavailable data
      const stats = [];

      if (cagr !== null && cagr !== undefined) {
        stats.push({
          label: 'CAGR',
          value: cagrDisplay || safeFormat(cagr, formatPercent)
        });
      }

      if (latestRevenue !== null && latestRevenue !== undefined) {
        stats.push({
          label: 'Latest Revenue',
          value: latestRevenueDisplay || safeFormat(latestRevenue, formatBillions)
        });
      }

      if (annualGrowthRate !== null && annualGrowthRate !== undefined) {
        stats.push({
          label: 'Annual Growth',
          value: annualGrowthRateDisplay || safeFormat(annualGrowthRate, formatPercent)
        });
      }

      if (stats.length > 0) {
        subsections.push({
          component: 'stat-cards',
          content: { stats }
        });
      }
    }

    // Add chart if series data available
    if (data.marketGrowthTrend.trend?.series?.length > 0) {
      subsections.push({
        component: 'chart-line',
        content: {
          data: data.marketGrowthTrend.trend.series,
          config: {
            xKey: 'year',
            yKey: 'revenue',
            yAxisLabel: 'Revenue ($B)',
            formatY: (value) => `$${(value / 1000000000).toFixed(0)}`,
            height: 350,
            showDots: true,
            showGrid: true,
            showLegend: false,
            color: '#10b981'
          }
        }
      });
    }

    sections.push({
      id: 'marketGrowthTrend',
      title: 'Market Growth Trend',
      component: 'markdown',
      content: data.marketGrowthTrend.overview || '5-year historical and projected growth analysis',
      subsections,
      icon: getIconForSection('marketGrowthTrend')
    });
  }

  // Market Size - with stat cards
  if (data.marketSize) {
    const {
      totalAddressableMarket,
      servicableMarket,
      shareOfMarket,
      totalAddressableMarketDisplay,
      servicableMarketDisplay,
      shareOfMarketDisplay,
      confidence,
      notes
    } = data.marketSize;

    const stats = [];

    if (totalAddressableMarket !== null && totalAddressableMarket !== undefined) {
      stats.push({
        label: 'Total Addressable Market',
        value: totalAddressableMarketDisplay || safeFormat(totalAddressableMarket, formatBillions)
      });
    }

    if (servicableMarket !== null && servicableMarket !== undefined) {
      stats.push({
        label: 'Serviceable Market',
        value: servicableMarketDisplay || safeFormat(servicableMarket, formatBillions)
      });
    }

    if (shareOfMarket !== null && shareOfMarket !== undefined) {
      stats.push({
        label: 'Share of Market',
        value: shareOfMarketDisplay || safeFormat(shareOfMarket, formatMillions)
      });
    }

    const sizeSubsections = [];

    if (stats.length > 0) {
      sizeSubsections.push({
        component: 'stat-cards',
        content: { stats }
      });
    }

    // Add confidence and notes if available
    if (confidence || notes) {
      const confidenceMd = [];
      if (confidence) {
        confidenceMd.push(`**Confidence:** ${confidence}`);
      }
      if (notes) {
        confidenceMd.push(`\n${notes}`);
      }
      sizeSubsections.push({
        component: 'markdown',
        content: confidenceMd.join('\n')
      });
    }

    if (sizeSubsections.length > 0) {
      sections.push({
        id: 'marketSize',
        title: 'Market Size',
        icon: getIconForSection('marketSize'),
        component: 'markdown',
        content:
          data.marketSize.overview || 'Total addressable, serviceable, and target market sizing.',
        subsections: sizeSubsections
      });
    }
  }

  // Market Trends - markdown with formatted list
  if (data.marketTrends?.trends?.length > 0) {
    const content = [
      data.marketTrends.overview,
      '',
      ...data.marketTrends.trends.map((t) => `${t.title}\n${t.description}`)
    ]
      .filter(Boolean)
      .join('\n\n');

    sections.push({
      id: 'marketTrends',
      title: 'Market Trends',
      component: 'markdown',
      content,
      icon: getIconForSection('marketTrends')
    });
  }

  // Customer Segmentation - markdown
  if (data.customerSegmentation?.segments?.length > 0) {
    const content = [
      data.customerSegmentation.overview,
      '',
      ...data.customerSegmentation.segments.map(
        (s) => `${s.segmentName} (${s.percentage}%)\n${s.description}`
      )
    ]
      .filter(Boolean)
      .join('\n\n');

    sections.push({
      id: 'customerSegmentation',
      title: 'Customer Segmentation',
      component: 'markdown',
      content,
      icon: getIconForSection('customerSegmentation')
    });
  }

  // Competitive Landscape - with comparison table and market share chart
  if (data.competitiveLandscape) {
    const subsections = [];

    // Add comparison matrix table
    if (
      data.competitiveLandscape.yourCompany &&
      data.competitiveLandscape.competitors?.length > 0
    ) {
      // Helper function to format price level
      const formatPriceLevel = (level) => {
        if (level === 'Premium') return '$$$';
        if (level === 'Mid-Tier') return '$$';
        return '$';
      };

      // Build headers: Metric, Your Company, ...Competitors
      const headers = [
        'Metric',
        data.competitiveLandscape.yourCompany.name,
        ...data.competitiveLandscape.competitors.map((c) => c.name)
      ];

      // Build rows for each metric
      const rows = [
        [
          'Price Level',
          formatPriceLevel(data.competitiveLandscape.yourCompany.priceLevel),
          ...data.competitiveLandscape.competitors.map((c) => formatPriceLevel(c.priceLevel))
        ],
        [
          'Quality Rating',
          `${data.competitiveLandscape.yourCompany.qualityRating}/5`,
          ...data.competitiveLandscape.competitors.map((c) => `${c.qualityRating}/5`)
        ],
        [
          'Customer Satisfaction',
          `${data.competitiveLandscape.yourCompany.customerSatisfaction}/5`,
          ...data.competitiveLandscape.competitors.map((c) => `${c.customerSatisfaction}/5`)
        ],
        [
          'Market Presence',
          data.competitiveLandscape.yourCompany.marketPresence,
          ...data.competitiveLandscape.competitors.map((c) => c.marketPresence)
        ]
      ];

      subsections.push({
        title: 'Competitive Comparison Matrix',
        component: 'table',
        content: {
          headers,
          rows,
          columnStyles: { 1: 'cell' } // Highlight "Your Company" column
        }
      });
    }

    // Add market share bar chart
    if (
      data.competitiveLandscape.yourCompany &&
      data.competitiveLandscape.competitors?.length > 0
    ) {
      // Prepare data for bar chart
      const chartData = [
        {
          name: data.competitiveLandscape.yourCompany.name,
          marketShare: data.competitiveLandscape.yourCompany.marketShare * 100,
          category: 'Your company'
        },
        ...data.competitiveLandscape.competitors.map((c) => ({
          name: c.name,
          marketShare: c.marketShare * 100,
          category: 'Competitors'
        }))
      ];

      subsections.push({
        title: 'Market Share Distribution',
        component: 'chart-bar',
        content: {
          data: chartData,
          config: {
            xKey: 'name',
            yKey: 'marketShare',
            colorKey: 'category',
            colorMap: {
              'Your company': '#10b981', // Green
              Competitors: '#1e293b' // Dark
            },
            yAxisLabel: 'Market Share (%)',
            height: 350,
            formatY: (value) => `${value.toFixed(0)}%`,
            showLegend: true
          }
        }
      });
    }

    sections.push({
      id: 'competitiveLandscape',
      title: 'Competitive Landscape',
      component: 'markdown',
      content:
        data.competitiveLandscape.overview ||
        'Understanding your competitive environment helps identify opportunities for differentiation and growth.',
      subsections,
      icon: getIconForSection('competitiveLandscape')
    });
  }

  // Competitor Profiles - detailed cards
  if (data.competitorProfiles?.profiles?.length > 0) {
    const subsections = [];

    // Create a detailed profile for each competitor
    data.competitorProfiles.profiles.forEach((profile) => {
      const profileContent = [];

      // Basic info
      if (profile.description) {
        profileContent.push(profile.description);
      }

      // Value proposition
      if (profile.valueProposition) {
        profileContent.push(`\n**Value Proposition:** ${profile.valueProposition}`);
      }

      // Established year
      if (profile.established) {
        profileContent.push(`\n**Established:** ${profile.established}`);
      }

      // Strengths
      if (profile.strengths?.length > 0) {
        profileContent.push(
          `\n**Strengths:**\n${profile.strengths.map((s) => `- ${s}`).join('\n')}`
        );
      }

      // Weaknesses
      if (profile.weaknesses?.length > 0) {
        profileContent.push(
          `\n**Weaknesses:**\n${profile.weaknesses.map((w) => `- ${w}`).join('\n')}`
        );
      }

      // Key differentiators
      if (profile.keyDifferentiators?.length > 0) {
        profileContent.push(
          `\n**Key Differentiators:**\n${profile.keyDifferentiators.map((d) => `- ${d}`).join('\n')}`
        );
      }

      // Verification status (from grounding)
      if (profile.verified !== undefined) {
        const badge = profile.verified ? '✓ Verified' : '⚠️ Unverified';
        profileContent.push(`\n*${badge}*`);
      }

      if (profileContent.length > 0) {
        subsections.push({
          title: profile.name || 'Unknown Competitor',
          component: 'markdown',
          content: profileContent.join('\n')
        });
      }
    });

    sections.push({
      id: 'competitorProfiles',
      title: 'Competitor Profiles',
      component: 'markdown',
      content:
        data.competitorProfiles.overview || 'Detailed profiles of key competitors in your market.',
      subsections,
      icon: getIconForSection('competitorProfiles')
    });
  }

  // SWOT Analysis - table
  if (data.swotAnalysis) {
    sections.push({
      id: 'swotAnalysis',
      title: 'SWOT Analysis',
      component: 'table',
      content: {
        caption: data.swotAnalysis.overview,
        headers: ['Category', 'Items'],
        rows: [
          ['Strengths', data.swotAnalysis.strengths?.join(' • ') || 'N/A'],
          ['Weaknesses', data.swotAnalysis.weaknesses?.join(' • ') || 'N/A'],
          ['Opportunities', data.swotAnalysis.opportunities?.join(' • ') || 'N/A'],
          ['Threats', data.swotAnalysis.threats?.join(' • ') || 'N/A']
        ]
      },
      icon: getIconForSection('swotAnalysis')
    });
  }

  // Actionable Recommendations - markdown
  if (data.actionableRecommendations?.recommendations?.length > 0) {
    const content = [
      data.actionableRecommendations.overview,
      '',
      ...data.actionableRecommendations.recommendations.map((r) => `${r.title}\n${r.description}`)
    ]
      .filter(Boolean)
      .join('\n\n');

    sections.push({
      id: 'actionableRecommendations',
      title: 'Actionable Recommendations',
      component: 'markdown',
      content,
      icon: getIconForSection('actionableRecommendations')
    });
  }

  // ROI Impact - table with improved formatting
  if (data.roiImpactLayer?.recommendationsRoi?.length > 0) {
    // Format ROI value for display
    const formatRoi = (roi) => {
      if (roi?.expectedRoiDisplay) {
        return roi.expectedRoiDisplay;
      }
      if (roi?.expectedRoi !== null && roi?.expectedRoi !== undefined) {
        return `${roi.expectedRoi.toFixed(1)}x`;
      }
      return 'N/A';
    };

    // Format effort level for display
    const formatEffort = (roi) => {
      if (roi?.effortLevelDisplay) {
        return roi.effortLevelDisplay;
      }
      if (roi?.effortLevel !== null && roi?.effortLevel !== undefined) {
        const levels = ['Low', 'Medium', 'High', 'Very High'];
        return levels[Math.min(roi.effortLevel - 1, 3)] || 'N/A';
      }
      return 'N/A';
    };

    sections.push({
      id: 'roiImpactLayer',
      title: 'ROI Impact Analysis',
      component: 'table',
      content: {
        caption: data.roiImpactLayer.overview,
        headers: ['Recommendation', 'Expected ROI', 'Effort Level', 'Category'],
        rows: data.roiImpactLayer.recommendationsRoi.map((r) => [
          r.title || 'Unnamed',
          formatRoi(r),
          formatEffort(r),
          r.roiCategory || 'N/A'
        ])
      },
      icon: getIconForSection('roiImpactLayer')
    });
  }

  // Implementation Guidance - markdown with timeline
  if (data.implementationGuidance?.steps?.length > 0) {
    const contentParts = [];

    // Overview
    if (data.implementationGuidance.overview) {
      contentParts.push(data.implementationGuidance.overview);
    }

    // Timeline if available
    if (data.implementationGuidance.timeline) {
      contentParts.push(`**Timeline:** ${data.implementationGuidance.timeline}`);
    }

    // Steps
    contentParts.push('');
    data.implementationGuidance.steps.forEach((s) => {
      let stepContent = `**Step ${s.stepNumber}: ${s.title}**\n${s.description}`;

      // Add timeline for individual step if available
      if (s.timeline) {
        stepContent += `\n*Timeline: ${s.timeline}*`;
      }

      // Add resources if available
      if (s.resources?.length > 0) {
        stepContent += `\n*Resources: ${s.resources.join(', ')}*`;
      }

      contentParts.push(stepContent);
    });

    // Notes if available
    if (data.implementationGuidance.notes) {
      contentParts.push(`\n*Note: ${data.implementationGuidance.notes}*`);
    }

    const content = contentParts.filter(Boolean).join('\n\n');

    sections.push({
      id: 'implementationGuidance',
      title: 'Implementation Roadmap',
      component: 'markdown',
      content,
      icon: getIconForSection('implementationGuidance')
    });
  }

  // Suggested Next Steps - markdown
  if (data.suggestedNextSteps?.steps?.length > 0) {
    const content = [
      data.suggestedNextSteps.overview,
      data.suggestedNextSteps.introCallout,
      '',
      ...data.suggestedNextSteps.steps.map((s) => `- ${s}`)
    ]
      .filter(Boolean)
      .join('\n');

    sections.push({
      id: 'suggestedNextSteps',
      title: 'Suggested Next Steps (30-60 Days)',
      component: 'markdown',
      content,
      icon: getIconForSection('suggestedNextSteps')
    });
  }

  // Methodology - markdown with confidence
  if (data.methodology?.dataSources?.length > 0 || data.methodology?.overview) {
    const contentParts = [];

    // Overview
    if (data.methodology.overview) {
      contentParts.push(data.methodology.overview);
    }

    // Intro text
    if (data.methodology.introText) {
      contentParts.push(data.methodology.introText);
    }

    // Confidence level
    if (data.methodology.confidence) {
      contentParts.push(`**Overall Confidence:** ${data.methodology.confidence}`);
    }

    // Data Sources
    if (data.methodology.dataSources?.length > 0) {
      contentParts.push('\n### Data Sources');
      data.methodology.dataSources.forEach((s) => {
        contentParts.push(`**${s.name}**\n${s.description}`);
      });
    }

    // Notes
    if (data.methodology.notes) {
      contentParts.push(`\n*Note: ${data.methodology.notes}*`);
    }

    // Limitations
    if (data.methodology.limitations?.length > 0) {
      contentParts.push('\n### Limitations');
      data.methodology.limitations.forEach((l) => {
        contentParts.push(`- ${l}`);
      });
    }

    const content = contentParts.filter(Boolean).join('\n\n');

    sections.push({
      id: 'methodology',
      title: 'Methodology & Data Sources',
      component: 'markdown',
      content,
      icon: null
    });
  }

  return sections;
}
